import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calculator, TrendUp, Trash, Info, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing() {
  const [sheets, setSheets] = useState([]);
  const { t } = useLanguage();
  const [selectedSheet, setSelectedSheet] = useState("");
  const [pricingMethod, setPricingMethod] = useState("profit_margin");
  const [value, setValue] = useState("");
  const [overheadCost, setOverheadCost] = useState("15");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricingHistory, setPricingHistory] = useState([]);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanationData, setExplanationData] = useState(null);
  const [sheetSearch, setSheetSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [sheetsRes, pricingRes] = await Promise.all([
        axios.get(`${API}/technical-sheets`),
        axios.get(`${API}/pricing`)
      ]);
      setSheets(sheetsRes.data);
      setPricingHistory(pricingRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCalculate = async () => {
    if (!selectedSheet || !value) {
      toast.error("Selecione uma ficha e informe o valor");
      return;
    }

    const numValue = parseFloat(value);
    
    if (pricingMethod === "profit_margin" && numValue >= 100) {
      toast.error("A margem de lucro deve ser estritamente menor que 100%. Use Markup para percentuais maiores.");
      return;
    }

    try {
      const response = await axios.post(`${API}/pricing`, {
        technical_sheet_id: selectedSheet,
        pricing_method: pricingMethod,
        value: numValue,
        overhead_cost: parseFloat(overheadCost) || 0
      });
      
      setResult(response.data);
      fetchData();
      toast.success("Precificação calculada e salva!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao calcular precificação");
    }
  };

  const handleDelete = async (pricingId) => {
    if (!window.confirm("Tem certeza que deseja deletar esta análise de preço?")) return;
    try {
      await axios.delete(`${API}/pricing/${pricingId}`);
      toast.success("Análise deletada com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao deletar análise");
    }
  };
  
  const fc = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  
  const getSelectedSheetData = () => sheets.find(s => s.id === selectedSheet);

  const buildExplanation = (data) => {
    const isMarkup = data.pricing_method === 'markup';
    const method = isMarkup ? 'Markup' : 'Margem de Lucro';
    const pct = data.value;
    const ingCost = round2(data.cost_price);
    const overhead = round2(data.overhead_cost || 0);
    const totalCost = round2(data.total_cost || (ingCost + overhead));
    const price = round2(data.selling_price);
    const profit = round2(price - totalCost);
    const margin = data.margin_percentage;

    const steps = [
      { label: 'Custo do ingrediente (ficha técnica)', value: fc(ingCost), formula: 'Custo Total ÷ Rendimento (arredondado para 2 casas)' },
      { label: 'Custos operacionais (mão de obra, luz, água)', value: fc(overhead), formula: 'Taxa fixa configurável' },
      { label: 'Custo total por unidade', value: fc(totalCost), formula: `${fc(ingCost)} + ${fc(overhead)} = ${fc(totalCost)}` },
      { label: `${method} aplicado`, value: `${pct.toFixed(1)}%`, formula: isMarkup ? 'Percentual sobre o custo total' : 'Percentual sobre o preço de venda' },
    ];

    if (isMarkup) {
      const multiplier = round4(1 + pct / 100);
      steps.push(
        { label: 'Cálculo do preço', value: fc(price), formula: `${fc(totalCost)} × (1 + ${pct.toFixed(1)}% ÷ 100) = ${fc(totalCost)} × ${multiplier} = ${fc(price)}` },
        { label: 'Lucro por unidade', value: fc(profit), formula: `${fc(price)} − ${fc(totalCost)} = ${fc(profit)}` },
        { label: 'Margem real sobre venda', value: `${margin.toFixed(1)}%`, formula: `(${fc(profit)} ÷ ${fc(price)}) × 100` },
      );
    } else {
      const divisor = round4(1 - pct / 100);
      steps.push(
        { label: 'Cálculo do preço', value: fc(price), formula: `${fc(totalCost)} ÷ (1 − ${pct.toFixed(1)}% ÷ 100) = ${fc(totalCost)} ÷ ${divisor} = ${fc(price)}` },
        { label: 'Lucro por unidade', value: fc(profit), formula: `${fc(price)} − ${fc(totalCost)} = ${fc(profit)}` },
        { label: 'Margem real sobre venda', value: `${margin.toFixed(1)}%`, formula: `(${fc(profit)} ÷ ${fc(price)}) × 100` },
      );
    }

    return {
      title: `Cálculo por ${method}`,
      steps,
      note: isMarkup
        ? 'No Markup, o percentual é aplicado sobre o CUSTO TOTAL (ingredientes + custos operacionais). Ex: 100% de markup = preço é o dobro do custo.'
        : 'Na Margem de Lucro, o percentual é sobre o PREÇO DE VENDA. Deve ser estritamente menor que 100%. Ex: 50% de margem = metade do preço é lucro.'
    };
  };

  const round2 = (v) => Math.round((v || 0) * 100) / 100;
  const round4 = (v) => Math.round(v * 10000) / 10000;

  const filteredSheets = sheets.filter(s => 
    s.name.toLowerCase().includes(sheetSearch.toLowerCase())
  );
  
  if (loading) {
    return <div className="text-center py-12 text-zinc-500">Carregando...</div>;
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">Precificação</h2>
        <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("pr_subtitle")}</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calculator Card */}
        <Card className="border-zinc-200 shadow-sm rounded-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold tracking-tight text-zinc-900 flex items-center">
              <Calculator size={20} className="mr-2" />
              Calculadora de Preço
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="sheet-select" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Ficha Técnica</Label>
              <div className="relative mt-1.5">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  data-testid="pricing-sheet-search"
                  value={sheetSearch}
                  onChange={(e) => setSheetSearch(e.target.value)}
                  placeholder="Buscar ficha técnica..."
                  className="pl-9"
                />
              </div>
              <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                <SelectTrigger data-testid="pricing-sheet-select" className="mt-1.5">
                  <SelectValue placeholder="Selecione uma ficha técnica..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredSheets.map((sheet) => (
                    <SelectItem key={sheet.id} value={sheet.id}>
                      {sheet.name} - Custo: {fc(sheet.cost_per_unit)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedSheet && (
              <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Custo Ingredientes</p>
                    <p className="font-mono text-lg font-bold text-zinc-900 mt-1" data-testid="cost-per-unit-display">
                      {fc(round2(getSelectedSheetData()?.cost_per_unit || 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Rendimento</p>
                    <p className="font-mono text-lg font-bold text-zinc-900 mt-1">
                      {getSelectedSheetData()?.yield_quantity} {getSelectedSheetData()?.yield_unit}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Custos Operacionais */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                Custos Operacionais (R$ por unidade)
              </Label>
              <Input
                data-testid="overhead-cost-input"
                type="number"
                step="0.01"
                value={overheadCost}
                onChange={(e) => setOverheadCost(e.target.value)}
                placeholder="15.00"
                className="mt-1.5"
              />
              <p className="text-xs text-zinc-500 mt-1">Mão de obra, energia, água, embalagem, etc. Somado ao custo dos ingredientes.</p>
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-3 block">Método de Precificação</Label>
              <Tabs value={pricingMethod} onValueChange={(v) => { setPricingMethod(v); setValue(""); }} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="profit_margin" data-testid="profit-margin-tab">Margem de Lucro</TabsTrigger>
                  <TabsTrigger value="markup" data-testid="markup-tab">Markup</TabsTrigger>
                </TabsList>
                
                <TabsContent value="profit_margin" className="mt-4">
                  <div>
                    <Label htmlFor="margin-value" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Margem de Lucro (%)</Label>
                    <Input
                      id="margin-value"
                      data-testid="margin-value-input"
                      type="number"
                      step="0.01"
                      max="99.99"
                      value={value}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (v >= 100) {
                          toast.error("A margem de lucro deve ser menor que 100%");
                          return;
                        }
                        setValue(e.target.value);
                      }}
                      placeholder="Ex: 30"
                      className="mt-1.5"
                    />
                    <p className="text-xs text-zinc-500 mt-2">Calculada sobre o preço de venda. Deve ser estritamente menor que 100%.</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="markup" className="mt-4">
                  <div>
                    <Label htmlFor="markup-value" className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Markup (%)</Label>
                    <Input
                      id="markup-value"
                      data-testid="markup-value-input"
                      type="number"
                      step="0.01"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder="Ex: 50"
                      className="mt-1.5"
                    />
                    <p className="text-xs text-zinc-500 mt-2">Calculado sobre o custo total (ingredientes + operacional). Até 500%.</p>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            <Button 
              data-testid="calculate-price-btn"
              onClick={handleCalculate}
              className="w-full bg-zinc-950 text-white hover:bg-zinc-800"
            >
              <TrendUp size={18} className="mr-2" />
              Calcular Preço
            </Button>
          </CardContent>
        </Card>
        
        {/* Results Card */}
        <Card className="border-zinc-200 shadow-sm rounded-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold tracking-tight text-zinc-900">
              Resultados
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-6">
                <div className="p-6 bg-green-50 border-2 border-green-200 rounded-sm">
                  <p className="text-xs uppercase tracking-wider text-green-700 font-semibold mb-2">Preço de Venda Sugerido</p>
                  <p className="text-4xl font-mono font-bold text-green-700" data-testid="selling-price-result">
                    {fc(result.selling_price)}
                  </p>
                  <p className="text-sm text-green-600 mt-1">por {getSelectedSheetData()?.yield_unit?.slice(0, -1) || 'unidade'}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Custo Ingredientes</p>
                    <p className="text-xl font-mono font-bold text-zinc-900 mt-2" data-testid="cost-price-result">
                      {fc(result.cost_price)}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Custos Operacionais</p>
                    <p className="text-xl font-mono font-bold text-zinc-900 mt-2">
                      {fc(result.overhead_cost || 0)}
                    </p>
                  </div>

                  <div className="p-4 bg-zinc-100 border border-zinc-300 rounded-sm">
                    <p className="text-xs uppercase tracking-wider text-zinc-600 font-semibold">Custo Total</p>
                    <p className="text-xl font-mono font-bold text-zinc-900 mt-2">
                      {fc(result.total_cost || (result.cost_price + (result.overhead_cost || 0)))}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Lucro/Unidade</p>
                    <p className="text-xl font-mono font-bold text-green-600 mt-2" data-testid="profit-per-unit-result">
                      {fc(result.profit_per_unit)}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm col-span-2">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Margem Real sobre Venda</p>
                    <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-2" data-testid="margin-result">
                      {result.margin_percentage.toFixed(2)}%
                    </p>
                  </div>
                </div>
                
                {result.margin_percentage < 20 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                    <p className="text-sm font-medium text-amber-800">
                      Margem baixa! Considere aumentar o preço ou reduzir custos.
                    </p>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setExplanationData(buildExplanation(result)); setShowExplanation(true); }}
                  className="w-full"
                  data-testid="show-calc-explanation"
                >
                  <Info size={16} className="mr-2" />
                  Entender o cálculo
                </Button>
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <Calculator size={32} className="mx-auto mb-3 text-zinc-300" />
                <p className="text-sm">Selecione uma ficha e calcule o preço</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* History */}
      <Card className="border-zinc-200 shadow-sm rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold tracking-tight text-zinc-900">Histórico de Precificação</CardTitle>
        </CardHeader>
        <CardContent>
          {pricingHistory.length === 0 ? (
            <div className="text-center py-8 text-zinc-400 text-sm">Nenhuma precificação salva.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Produto</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Método</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right">Custo Total</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right">Preço Venda</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right">Lucro</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right">Margem</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricingHistory.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium text-zinc-900">{p.technical_sheet_name}</TableCell>
                    <TableCell className="text-zinc-700 text-xs">
                      <span className="px-2 py-0.5 bg-zinc-100 rounded">
                        {p.pricing_method === 'markup' ? 'Markup' : 'Margem'} {p.value.toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-zinc-900">{fc(p.total_cost || p.cost_price)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-zinc-900">{fc(p.selling_price)}</TableCell>
                    <TableCell className="text-right font-mono text-green-600">{fc(p.profit_per_unit)}</TableCell>
                    <TableCell className="text-right font-mono text-zinc-900">{p.margin_percentage.toFixed(1)}%</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"
                          onClick={() => { setExplanationData(buildExplanation(p)); setShowExplanation(true); }}
                          title="Ver cálculo"
                        >
                          <Info size={14} />
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                          onClick={() => handleDelete(p.id)}
                          data-testid={`delete-pricing-${p.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Explanation Modal */}
      <Dialog open={showExplanation} onOpenChange={setShowExplanation}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">{explanationData?.title}</DialogTitle>
            <DialogDescription className="sr-only">Detalhes de precificação</DialogDescription>
          </DialogHeader>
          {explanationData && (
            <div className="space-y-3 mt-2">
              {explanationData.steps.map((step, idx) => (
                <div key={step.label} className="p-3 bg-zinc-50 rounded-lg border border-zinc-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-zinc-600 uppercase tracking-wide">
                      {idx + 1}. {step.label}
                    </span>
                    <span className="text-sm font-mono font-bold text-zinc-900">{step.value}</span>
                  </div>
                  <p className="text-xs text-zinc-500 font-mono">{step.formula}</p>
                </div>
              ))}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">{explanationData.note}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
